import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrganizations, fetchRegions, createOrganization, updateOrganization, deleteOrganization } from '../store/slices/organizationsSlice';
import Modal from '../components/common/Modal';
import { Loading, EmptyState, Pagination, ConfirmDialog } from '../components/common';
import toast from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon, BuildingOfficeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const Organizations = () => {
  const dispatch = useDispatch();
  const { organizations, regions, loading } = useSelector((state) => state.organizations);
  const { user } = useSelector((state) => state.auth);
  
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [search, setSearch] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  
  const [formData, setFormData] = useState({
    name: '', short_name: '', code: '', region: '', address: '', phone: '', email: '', website: '', rector_name: '', status: 'active',
  });

  useEffect(() => {
    dispatch(fetchOrganizations());
    dispatch(fetchRegions());
  }, [dispatch]);

  const filteredOrgs = organizations.filter(org => {
    const matchSearch = org.name.toLowerCase().includes(search.toLowerCase()) || org.short_name?.toLowerCase().includes(search.toLowerCase()) || org.code?.toLowerCase().includes(search.toLowerCase());
    const matchRegion = !regionFilter || org.region === parseInt(regionFilter);
    return matchSearch && matchRegion;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedOrg) {
        await dispatch(updateOrganization({ id: selectedOrg.id, data: formData })).unwrap();
        toast.success('Tashkilot yangilandi!');
      } else {
        await dispatch(createOrganization(formData)).unwrap();
        toast.success('Tashkilot yaratildi!');
      }
      setShowModal(false);
      resetForm();
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const handleEdit = (org) => {
    setSelectedOrg(org);
    setFormData({
      name: org.name, short_name: org.short_name || '', code: org.code, region: org.region || '',
      address: org.address || '', phone: org.phone || '', email: org.email || '', website: org.website || '',
      rector_name: org.rector_name || '', status: org.status,
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteOrganization(selectedOrg.id)).unwrap();
      toast.success('Tashkilot o\'chirildi!');
      setShowDeleteModal(false);
      setSelectedOrg(null);
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const resetForm = () => {
    setSelectedOrg(null);
    setFormData({ name: '', short_name: '', code: '', region: '', address: '', phone: '', email: '', website: '', rector_name: '', status: 'active' });
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Tashkilotlar</h1>
          <p className="text-gray-500">Jami {organizations.length} ta tashkilot</p>
        </div>
        {isAdmin && (
          <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary">
            <PlusIcon className="w-5 h-5" /> Yangi tashkilot
          </button>
        )}
      </div>

      <div className="card">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Qidirish..." className="input pl-10" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          <select className="select w-auto min-w-[180px]" value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}>
            <option value="">Barcha viloyatlar</option>
            {regions.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
          </select>
        </div>
      </div>

      {loading ? <Loading /> : filteredOrgs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOrgs.map((org) => (
            <div key={org.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <BuildingOfficeIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{org.short_name || org.name}</h3>
                    <p className="text-sm text-gray-500">{org.code}</p>
                  </div>
                </div>
                <span className={`badge ${org.status === 'active' ? 'badge-success' : 'badge-gray'}`}>
                  {org.status === 'active' ? 'Faol' : 'Nofaol'}
                </span>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <p><span className="text-gray-400">Viloyat:</span> {org.region_name || '-'}</p>
                <p><span className="text-gray-400">Rektor:</span> {org.rector_name || '-'}</p>
                <p><span className="text-gray-400">Auditorlar:</span> {org.auditor_count || 0}</p>
              </div>
              {isAdmin && (
                <div className="flex gap-2 mt-4 pt-4 border-t">
                  <button onClick={() => handleEdit(org)} className="btn-ghost btn-sm flex-1">
                    <PencilIcon className="w-4 h-4" /> Tahrirlash
                  </button>
                  <button onClick={() => { setSelectedOrg(org); setShowDeleteModal(true); }} className="btn-ghost btn-sm text-red-600">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={BuildingOfficeIcon} title="Tashkilotlar topilmadi" description="Yangi tashkilot qo'shing" />
      )}

      {/* Create/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={selectedOrg ? 'Tashkilotni tahrirlash' : 'Yangi tashkilot'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="input-group sm:col-span-2">
              <label className="label">To'liq nomi *</label>
              <input type="text" className="input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div className="input-group">
              <label className="label">Qisqa nomi</label>
              <input type="text" className="input" value={formData.short_name} onChange={(e) => setFormData({ ...formData, short_name: e.target.value })} />
            </div>
            <div className="input-group">
              <label className="label">Kodi *</label>
              <input type="text" className="input" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} required />
            </div>
            <div className="input-group">
              <label className="label">Viloyat</label>
              <select className="select" value={formData.region} onChange={(e) => setFormData({ ...formData, region: e.target.value })}>
                <option value="">Tanlang</option>
                {regions.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
              </select>
            </div>
            <div className="input-group">
              <label className="label">Holat</label>
              <select className="select" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                <option value="active">Faol</option>
                <option value="inactive">Nofaol</option>
              </select>
            </div>
            <div className="input-group sm:col-span-2">
              <label className="label">Manzil</label>
              <input type="text" className="input" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
            </div>
            <div className="input-group">
              <label className="label">Telefon</label>
              <input type="text" className="input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            </div>
            <div className="input-group">
              <label className="label">Email</label>
              <input type="email" className="input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            </div>
            <div className="input-group">
              <label className="label">Veb-sayt</label>
              <input type="url" className="input" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
            </div>
            <div className="input-group">
              <label className="label">Rektor F.I.SH</label>
              <input type="text" className="input" value={formData.rector_name} onChange={(e) => setFormData({ ...formData, rector_name: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Bekor qilish</button>
            <button type="submit" className="btn-primary">{selectedOrg ? 'Saqlash' : 'Yaratish'}</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} onConfirm={handleDelete}
        title="Tashkilotni o'chirish" message={`"${selectedOrg?.name}" tashkilotini o'chirishni xohlaysizmi?`} confirmText="O'chirish" />
    </div>
  );
};

export default Organizations;
